import type { MessageId } from '@/shared/i18n/catalogue.en'
import {
  AI_PROMPT_CAPABILITIES,
  CAPABILITIES,
  PRINT_FORM_REGISTRY_CAPABILITIES,
  canOpenPrintFormEditor,
  hasAnyCapability,
  hasCapability,
  isDeveloperSession,
  type CapabilityName,
} from '@/shared/session/capabilities'
import type { Session } from '@/shared/session/types'
import type { IconName } from '@/shared/ui/icons'

export const CONTROL_CENTER_SECTIONS = [
  'organization',
  'branches',
  'registration',
  'quotas',
  'legal-entities',
  'uploads',
  'users',
  'workplaces',
  'roles',
  'settings',
  'release-history',
  'terminology',
  'modules',
  'connectors',
  'print-forms',
  'protocols',
  'ai',
] as const

export type ControlCenterSection = (typeof CONTROL_CENTER_SECTIONS)[number]

export function isControlCenterSection(value: unknown): value is ControlCenterSection {
  return (
    typeof value === 'string' &&
    (CONTROL_CENTER_SECTIONS as readonly string[]).includes(value)
  )
}

export const AI_VIEWS = ['settings', 'files', 'credentials', 'tools'] as const
export type AIView = (typeof AI_VIEWS)[number]

export const AI_VIEW_META: Record<
  AIView,
  { label: MessageId; description: MessageId; icon: IconName }
> = {
  tools: { label: 'controlCenter.administration.aiTools', description: 'controlCenter.administration.aiToolsDescription', icon: 'bug' },
  settings: {
    label: 'ai.views.settings',
    description: 'ai.views.settingsDescription',
    icon: 'sliders',
  },
  files: {
    label: 'ai.views.files',
    description: 'ai.views.filesDescription',
    icon: 'folder',
  },
  credentials: {
    label: 'ai.views.credentials',
    description: 'ai.views.credentialsDescription',
    icon: 'key',
  },
}

const CONTROL_CENTER_SECTION_VIEWS: Partial<
  Record<ControlCenterSection, readonly string[]>
> = {
  ai: ['settings'],
}

export function controlCenterSectionViews(section: unknown): readonly string[] | null {
  return isControlCenterSection(section)
    ? (CONTROL_CENTER_SECTION_VIEWS[section] ?? null)
    : null
}

export function defaultControlCenterView(section: unknown): string | null {
  return controlCenterSectionViews(section)?.[0] ?? null
}

export function resolveAIView(view: unknown): AIView {
  return (AI_VIEWS as readonly string[]).includes(view as string)
    ? (view as AIView)
    : 'settings'
}

export const MODULE_ADMIN_VIEWS = ['title', 'settings', 'debug'] as const
export type ModuleAdminView = (typeof MODULE_ADMIN_VIEWS)[number]

export const MODULE_ADMIN_VIEW_META: Record<
  ModuleAdminView,
  { label: MessageId; description: MessageId; icon: IconName }
> = {
  title: {
    label: 'clinicModules.views.title',
    description: 'clinicModules.views.titleDescription',
    icon: 'document',
  },
  settings: {
    label: 'clinicModules.views.settings',
    description: 'clinicModules.views.settingsDescription',
    icon: 'sliders',
  },
  debug: {
    label: 'clinicModules.views.debug',
    description: 'clinicModules.views.debugDescription',
    icon: 'bug',
  },
}

export function isModuleAdminView(value: unknown): value is ModuleAdminView {
  return typeof value === 'string' &&
    (MODULE_ADMIN_VIEWS as readonly string[]).includes(value)
}

export function canAccessModuleAdminView(session: Session | undefined, view: ModuleAdminView): boolean {
  return canAccessControlCenterSection(session, 'modules') && (view !== 'settings' || isDeveloperSession(session))
}

export function resolveModuleAdminView(value: unknown): ModuleAdminView {
  return isModuleAdminView(value) ? value : 'title'
}

export const CONTROL_CENTER_SECTION_META: Record<
  ControlCenterSection,
  { label: MessageId; description: MessageId; icon: IconName }
> = {
  branches: { label: 'branches.title', description: 'branches.subtitle', icon: 'branch' },
  registration: { label: 'controlCenter.administration.registration', description: 'controlCenter.administration.registrationDescription', icon: 'user-plus' },
  quotas: { label: 'quotas.title', description: 'quotas.description', icon: 'coins' },
  organization: {
    label: 'controlCenter.nav.organization',
    description: 'controlCenter.nav.organizationDescription',
    // The clinic as a place; the identity card it wore is the patient's title
    // sheet. Every section of this column wears a picture of its own, because
    // the rail folds the words away and a column of three sheets and two
    // gears was a column read by its words.
    icon: 'hospital',
  },
  /* The register of identities the clinic contracts as, next to the clinic
     itself above it. */
  'legal-entities': {
    label: 'legalEntity.title',
    description: 'legalEntity.subtitle',
    icon: 'building',
  },
  uploads: {
    label: 'controlCenter.nav.uploads',
    description: 'controlCenter.nav.uploadsDescription',
    icon: 'upload',
  },
  users: {
    label: 'controlCenter.nav.users',
    description: 'controlCenter.nav.usersDescription',
    icon: 'person',
  },
  /* Who works where, beside the employees themselves: the rows are the
     organisation's, one per clinician and branch, and they decide which
     clinician a branch's schedule may book. */
  workplaces: {
    label: 'controlCenter.nav.workplaces',
    description: 'controlCenter.nav.workplacesDescription',
    icon: 'briefcase-medical',
  },
  roles: {
    label: 'controlCenter.nav.roles',
    description: 'controlCenter.nav.rolesDescription',
    icon: 'shield-check',
  },
  'release-history': { label: 'controlCenter.history.title', description: 'controlCenter.history.description', icon: 'history' },
  settings: {
    label: 'controlCenter.nav.settings',
    description: 'controlCenter.nav.settingsDescription',
    // Sliders inside; the gear is the Control Center's door on the rail.
    icon: 'sliders',
  },
  terminology: {
    label: 'controlCenter.nav.terminology',
    description: 'controlCenter.nav.terminologyDescription',
    icon: 'book-open',
  },
  /* Clinic administration, so it stands with the clinic sections and before
     the developer-only registries that close the list. */
  modules: {
    label: 'controlCenter.nav.modules',
    description: 'controlCenter.nav.modulesDescription',
    icon: 'puzzle',
  },
  /* Shared physical connectors are developer-only shard administration. */
  connectors: {
    label: 'controlCenter.nav.connectors',
    description: 'controlCenter.nav.connectorsDescription',
    icon: 'plug',
  },
  'print-forms': {
    label: 'controlCenter.nav.printForms',
    description: 'controlCenter.nav.printFormsDescription',
    icon: 'printer',
  },
  protocols: {
    label: 'controlCenter.nav.protocols',
    description: 'controlCenter.nav.protocolsDescription',
    icon: 'scroll-text',
  },
  ai: {
    label: 'controlCenter.nav.ai',
    description: 'controlCenter.nav.aiDescription',
    icon: 'sparkle',
  },
}

/** Technical entry labels differ from the clinic's operational registries. */
export const PLATFORM_SECTION_META: Partial<typeof CONTROL_CENTER_SECTION_META> = {
  ai: { ...CONTROL_CENTER_SECTION_META.ai, label: 'controlCenter.administration.platformAI' },
  'print-forms': { ...CONTROL_CENTER_SECTION_META['print-forms'], label: 'controlCenter.administration.platformPrintForms' },
  terminology: { ...CONTROL_CENTER_SECTION_META.terminology, label: 'controlCenter.administration.platformTerminology' },
  quotas: { ...CONTROL_CENTER_SECTION_META.quotas, label: 'controlCenter.administration.platformQuotas' },
}

export function showsAI(session: Session | undefined): boolean {
  return hasAnyCapability(session, AI_PROMPT_CAPABILITIES)
}

export function showsProtocols(session: Session | undefined): boolean {
  return isDeveloperSession(session) && hasCapability(session, CAPABILITIES.protocolsRead)
}

/**
 * Roles and access: exact developer kind AND the read grant. Core refuses the
 * whole `/api/v1/admin/authorization` family to a human account carrying the
 * string, so offering the tile on the capability alone would lead to a 403;
 * the route runs this same predicate and renders Not Found, fetching nothing.
 */
export function showsRoles(session: Session | undefined): boolean {
  return (
    isDeveloperSession(session) &&
    hasCapability(session, CAPABILITIES.authorizationRolesRead)
  )
}

export function showsPrintForms(session: Session | undefined): boolean {
  return (
    isDeveloperSession(session) &&
    (hasAnyCapability(session, PRINT_FORM_REGISTRY_CAPABILITIES) ||
      canOpenPrintFormEditor(session))
  )
}

/* Every section the predicate below answers with ONE grant. A section whose
   eligibility is a sentence rather than a grant — the four developer sections,
   and Uploads, which needs the chart as well as the study — is decided in the
   predicate and is absent here on purpose: an entry it never reads is a rule a
   reader would believe. */
const CONTROL_CENTER_SECTION_CAPABILITIES: Record<
  Exclude<ControlCenterSection, 'organization' | 'print-forms' | 'protocols' | 'ai' | 'roles' | 'uploads' | 'connectors' | 'settings'>,
  CapabilityName
> = {
  quotas: CAPABILITIES.clinicSettingsRead,
  branches: CAPABILITIES.clinicSettingsRead,
  registration: CAPABILITIES.clinicSettingsRead,
  'legal-entities': CAPABILITIES.legalEntitiesRead,
  users: CAPABILITIES.clinicUsersRead,
  /* The same directory read the Users section makes, at the grain of one
     workplace. Writing is each row's own server decision. */
  workplaces: CAPABILITIES.clinicUsersRead,
  'release-history': CAPABILITIES.clinicSettingsRead,
  terminology: CAPABILITIES.clinicSettingsRead,
  /* The generated read grant from the module catalogue contract (#199) and
     nothing inferred from it: a module's own business capabilities open its
     surfaces, never this catalogue, and an administrator role name is not a
     grant. Writing is the server's `canChangeState` plus the write grant,
     decided per item on the page. */
  modules: CAPABILITIES.clinicModulesRead,
}

export function canAccessControlCenterSection(
  session: Session | undefined,
  section: ControlCenterSection,
): boolean {
  if (section === 'organization') return hasAnyCapability(session, [CAPABILITIES.legalEntitiesRead, CAPABILITIES.clinicSettingsRead, CAPABILITIES.clinicSettingsWrite])
  if (section === 'print-forms') return showsPrintForms(session)
  if (section === 'protocols') return showsProtocols(session)
  if (section === 'ai') return showsAI(session)
  if (section === 'roles') return showsRoles(session)
  if (section === 'connectors') return isDeveloperSession(session) && hasCapability(session, CAPABILITIES.connectorsRead)
  if (section === 'settings') return hasCapability(session, CAPABILITIES.clinicSettingsRead)
  if (section === 'uploads') {
    return hasCapability(session, CAPABILITIES.patientsRead) &&
      hasCapability(session, CAPABILITIES.imagingStudiesRead)
  }
  return hasCapability(session, CONTROL_CENTER_SECTION_CAPABILITIES[section])
}

export function defaultControlCenterSection(
  session: Session | undefined,
): ControlCenterSection | null {
  return (
    CLINIC_MANAGEMENT_GROUPS.flatMap(group => group.sections).find((section) =>
      canAccessControlCenterSection(session, section),
    ) ?? null
  )
}

export const CLINIC_MANAGEMENT_GROUPS = [
  { label: 'controlCenter.administration.organizationGroup', sections: ['organization', 'branches', 'legal-entities', 'settings', 'quotas'] },
  { label: 'controlCenter.administration.teamGroup', sections: ['users', 'workplaces'] },
  { label: 'controlCenter.administration.formsGroup', sections: ['protocols', 'registration', 'print-forms', 'terminology'] },
  { label: 'controlCenter.administration.connectionsGroup', sections: ['modules', 'ai'] },
] as const satisfies readonly { label: MessageId; sections: readonly ControlCenterSection[] }[]

export type ClinicManagementSection = (typeof CLINIC_MANAGEMENT_GROUPS)[number]['sections'][number]

export const PLATFORM_SECTIONS = ['roles', 'connectors', 'ai', 'print-forms', 'terminology', 'quotas'] as const
export type PlatformSection = (typeof PLATFORM_SECTIONS)[number]

export function canAccessPlatformSection(session: Session | undefined, section: string): section is PlatformSection {
  return isDeveloperSession(session) && (PLATFORM_SECTIONS as readonly string[]).includes(section) &&
    canAccessControlCenterSection(session, section as PlatformSection)
}

export function defaultPlatformSection(session: Session | undefined): PlatformSection | null {
  return PLATFORM_SECTIONS.find(section => canAccessPlatformSection(session, section)) ?? null
}

